import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const FONCTIONS = ["Chef", "Gérant", "Acheteur", "Autre"];

export default function Register() {
    const { register } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        prenom: "",
        nom: "",
        email: "",
        password: "",
        raison_sociale: "",
        ide_tva: "",
        fonction: "Chef",
        telephone: "",
        consent: false,
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.consent) {
            setError("Vous devez accepter la politique de confidentialité pour continuer.");
            return;
        }
        setLoading(true);
        const res = await register(form);
        setLoading(false);
        if (res.ok) {
            toast.success("Demande envoyée. Vérifiez votre e-mail.");
            navigate("/en-attente");
        } else {
            setError(res.error);
        }
    };

    return (
        <section className="max-w-2xl mx-auto px-5 md:px-10 py-16 md:py-24" data-testid="register-page">
            <p className="bc-label-small">Devenir client Pro</p>
            <h1 className="font-serif text-[#D4BC7C] text-4xl sm:text-5xl mt-4">
                Demander un accès professionnel
            </h1>
            <div className="bc-hairline-gold my-6" />
            <p className="text-[#F2ECE0]/80 text-sm max-w-xl">
                Complétez le formulaire ci-dessous. Votre demande sera examinée par notre équipe.
                Vous recevrez une confirmation par e-mail dès validation.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-6" data-testid="register-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <label className="bc-label">Prénom</label>
                        <input required className="bc-input" value={form.prenom} onChange={set("prenom")} data-testid="reg-prenom" />
                    </div>
                    <div>
                        <label className="bc-label">Nom</label>
                        <input required className="bc-input" value={form.nom} onChange={set("nom")} data-testid="reg-nom" />
                    </div>
                </div>

                <div>
                    <label className="bc-label">E-mail professionnel</label>
                    <input
                        type="email"
                        required
                        className="bc-input"
                        value={form.email}
                        onChange={set("email")}
                        autoComplete="email"
                        data-testid="reg-email"
                    />
                </div>

                <div>
                    <label className="bc-label">Mot de passe (min. 8 caractères)</label>
                    <input
                        type="password"
                        required
                        minLength={8}
                        className="bc-input"
                        value={form.password}
                        onChange={set("password")}
                        autoComplete="new-password"
                        data-testid="reg-password"
                    />
                </div>

                <div className="bc-hairline my-2" />

                <div>
                    <label className="bc-label">Raison sociale</label>
                    <input required className="bc-input" value={form.raison_sociale} onChange={set("raison_sociale")} data-testid="reg-raison-sociale" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                        <label className="bc-label">Numéro IDE / TVA</label>
                        <input
                            required
                            placeholder="CHE-123.456.789"
                            className="bc-input"
                            value={form.ide_tva}
                            onChange={set("ide_tva")}
                            data-testid="reg-ide-tva"
                        />
                    </div>
                    <div>
                        <label className="bc-label">Fonction</label>
                        <select className="bc-input" value={form.fonction} onChange={set("fonction")} data-testid="reg-fonction">
                            {FONCTIONS.map((f) => (
                                <option key={f} value={f}>
                                    {f}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <label className="bc-label">Téléphone</label>
                    <input required className="bc-input" value={form.telephone} onChange={set("telephone")} data-testid="reg-telephone" />
                </div>

                <label className="flex items-start gap-3 text-sm text-[#F2ECE0]/85 cursor-pointer" data-testid="reg-consent-wrapper">
                    <input
                        type="checkbox"
                        checked={form.consent}
                        onChange={set("consent")}
                        className="mt-1 h-4 w-4 accent-[#D4BC7C] cursor-pointer"
                        data-testid="reg-consent"
                    />
                    <span>
                        J'accepte la{" "}
                        <a
                            href="https://boucherie-costa.ch/politique-de-confidentialite/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4BC7C] underline"
                        >
                            politique de confidentialité
                        </a>{" "}
                        et le traitement de mes données conformément au nLPD/RGPD.
                    </span>
                </label>

                {error && (
                    <div
                        className="border px-4 py-3 text-sm"
                        style={{ borderColor: "rgba(217,112,112,0.6)", color: "#ffb0b0", background: "#1a0a0a" }}
                        data-testid="reg-error"
                    >
                        {error}
                    </div>
                )}

                <button type="submit" className="bc-btn w-full" disabled={loading} data-testid="reg-submit">
                    {loading ? "Envoi en cours..." : "Envoyer ma demande"}
                </button>

                <p className="text-xs text-[#A0A0A0] text-center">
                    Vous avez déjà un compte ?{" "}
                    <Link to="/connexion" className="text-[#D4BC7C] hover:underline">
                        Se connecter
                    </Link>
                </p>
            </form>
        </section>
    );
}
